import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CAMPAIGN_MODE_LABEL, toCsv, type CampaignReportRow } from '@arcnet/contracts';
import { api } from '../../lib/api/client.js';
import { useSubroute } from '../../lib/subroute.js';
import { formatDuration, formatMoney, formatNumber } from '../../lib/format.js';
import { Badge, Empty, ErrorBanner, SectionHead, Segmented, TableSkeleton } from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';
import { CampaignAnalytics } from './CampaignAnalytics.js';
import { slugs } from './CampaignsPage.js';

/**
 * Reporting -> Campaigns (owner, 26 September 2026: "We should have all the reporting analytics
 * under reporting"). Every campaign side by side, and each one's analytics by its name:
 * /reporting/campaign-reports/march-renewals. The campaigns themselves are still set up under
 * Dialler.
 */

type Range = 'all' | 'today' | '7' | '30';
const RANGES: ReadonlyArray<readonly [Range, string]> = [['all', 'All time'], ['today', 'Today'], ['7', '7 days'], ['30', '30 days']];
const day = (d: Date): string => d.toLocaleDateString('en-CA');
function rangeQuery(r: Range): string {
  if (r === 'all') return '';
  const to = new Date();
  const from = new Date(to);
  if (r !== 'today') from.setDate(from.getDate() - (Number(r) - 1));
  return `?from=${day(from)}&to=${day(to)}`;
}
const pct = (n: number, of: number): string => (of ? `${Math.round((n / of) * 100)}%` : '—');
const tone = (n: number, of: number, good: number, fair: number): string =>
  !of ? 'fg-4' : n / of >= good ? 'ok' : n / of >= fair ? 'warn' : 'danger';
const STATUS_TONE: Record<string, 'ok' | 'warn' | 'muted' | 'info'> = { running: 'ok', paused: 'warn', draft: 'muted', finished: 'info' };

export function CampaignReportsPage({ tenantId }: { tenantId: string }) {
  const route = useSubroute();
  const [range, setRange] = useState<Range>('all');
  const q = useQuery({
    queryKey: ['dialler', tenantId, 'campaign-report', range],
    queryFn: () => api.get<{ data: CampaignReportRow[]; tz: string }>(`/v1/tenants/${tenantId}/campaign-report${rangeQuery(range)}`),
    placeholderData: (prev) => prev,
    refetchInterval: 60_000,
  });
  const rows = q.data?.data ?? [];
  const bySlug = slugs(rows);
  const open = route.rest[0];

  if (open) {
    const c = rows.find((x) => bySlug.get(x.id) === open) ?? null;
    return (
      <>
        <button type="button" className="arc-crumb" onClick={() => route.go([])} style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 0 }}>
          <Icon name="arrowLeft" size={15} /> Campaigns
        </button>
        {q.data && !c && <div className="arc-banner arc-banner-warn"><Icon name="warning" size={14} /><span>There is no campaign called {open}.</span></div>}
        {c && (
          <>
            <SectionHead title={c.name} sub={`${CAMPAIGN_MODE_LABEL[c.mode] ?? c.mode} campaign · ${c.status}`} />
            <CampaignAnalytics tenantId={tenantId} id={c.id} />
          </>
        )}
      </>
    );
  }

  const withCost = rows.some((r) => r.cost !== null);
  return (
    <>
      <SectionHead title="Campaigns" sub="How every campaign is doing. Open one for its full analytics." />
      <div className="arc-row" style={{ gap: 12, marginBottom: 12 }}>
        <Segmented label="Range" value={range} onChange={setRange} options={RANGES} />
        <div className="arc-spacer" />
        {rows.length > 0 && (
          <button type="button" className="arc-btn arc-btn-sm" onClick={() => download(`campaigns-${range}-${day(new Date())}.csv`, toCsv(
            ['campaign', 'mode', 'status', 'leads', 'reached', 'still to call', 'calls', 'reached a person', 'final', 'talk seconds', ...(withCost ? ['cost', 'currency'] : [])],
            rows.map((r) => [r.name, r.mode, r.status, r.leads, r.reached, r.remaining, r.calls, r.connected, r.final, r.talk_sec, ...(withCost ? [r.cost, r.currency] : [])]),
          ))}><Icon name="external" size={12} /> Export CSV</button>
        )}
      </div>
      {q.error && <ErrorBanner error={q.error} />}
      <div className="arc-card">
        <div className="arc-table-wrap">
          <table className="arc-table">
            <thead><tr><th>Campaign</th><th>Status</th><th className="arc-td-right">Leads reached</th><th className="arc-td-right">Calls</th>
              <th className="arc-td-right">Contact rate</th><th className="arc-td-right">Final</th><th className="arc-td-right">Talk time</th>
              {withCost && <th className="arc-td-right">Cost</th>}</tr></thead>
            {q.data === undefined ? <TableSkeleton cols={withCost ? 8 : 7} /> : (
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="arc-clickable" onClick={() => route.go([bySlug.get(r.id)!])}>
                    <td><span style={{ fontWeight: 570 }}>{r.name}</span><div className="arc-subtle" style={{ fontSize: 11.5 }}>{CAMPAIGN_MODE_LABEL[r.mode] ?? r.mode} · {formatNumber(r.remaining)} still to call</div></td>
                    <td><Badge tone={STATUS_TONE[r.status] ?? 'muted'}>{r.status}</Badge></td>
                    <td className="arc-td-right arc-num">
                      <div className="arc-row" style={{ gap: 8, justifyContent: 'flex-end' }}>
                        <span className="arc-meter" aria-hidden="true" style={{ width: 60 }}><span style={{ width: `${r.leads ? (r.reached / r.leads) * 100 : 0}%`, background: 'var(--arc-ok)' }} /></span>
                        {pct(r.reached, r.leads)}
                      </div>
                      <div className="arc-subtle" style={{ fontSize: 11.5 }}>{formatNumber(r.reached)} of {formatNumber(r.leads)}</div>
                    </td>
                    <td className="arc-td-right arc-num">{formatNumber(r.calls)}</td>
                    <td className="arc-td-right arc-num" style={{ color: `var(--arc-${tone(r.connected, r.calls, 0.35, 0.2)})`, fontWeight: 600 }}>{pct(r.connected, r.calls)}</td>
                    <td className="arc-td-right arc-num" style={{ color: r.final ? 'var(--arc-ok)' : undefined }}>{formatNumber(r.final)}</td>
                    <td className="arc-td-right arc-num">{formatDuration(r.talk_sec)}</td>
                    {withCost && <td className="arc-td-right arc-num">{r.cost === null ? '—' : formatMoney(r.cost.toFixed(2), r.currency)}</td>}
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>
        {q.data && rows.length === 0 && <Empty icon="callOut" title="No campaigns" hint="Campaigns are set up under Dialler." />}
      </div>
    </>
  );
}

function download(name: string, csv: string): void {
  const url = URL.createObjectURL(new Blob(['﻿', csv], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url; a.download = name;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
